<template>
  <div v-if="!loading" class="full-width full-height overflow-y-auto">
    <Stepper :steps="steps" step-color="third" :value="currentStep" class="mt-2 px-6" @input="changeCurrentStep" />
    <v-row no-gutters>
      <component :is="dynamicComponent" @next-step="changeCurrentStep" />
    </v-row>
  </div>
  <div v-else class="d-flex justify-center align-center full-height">
    <Loader />
  </div>
</template>

<script>
import { ref, provide, computed, onBeforeMount, watch, onMounted } from '@vue/composition-api';
import Client from '@/components/customerProduct/Client.vue';
import Product from '@/components/customerProduct/Product.vue';
import AssignConfirmation from '@/components/customerProduct/AssignConfirmation.vue';
import Stepper from '@/components/customerProduct/Stepper.vue';
import Loader from '@/components/common/Loader.vue';
import { sendNotification } from '@/helpers/utils';

import store from '@/store';

import { addAllowedProductsToCustomers } from '@/helpers/request';

export default {
  components: { Client, Product, AssignConfirmation, Loader, Stepper },
  setup () {
    const selectedAssignCustomer = ref([]);
    const selectedAssignProducts = ref([]);
    const loading = ref(false);
    const sendEmail = ref(false);

    const dynamicComponent = ref('Client');
    const currentStep = ref(1);

    const steps = ref([
      {
        index: 1,
        name: 'Sélection Clients',
        component: 'Client',
        tablesHeight: '350',
        visitedStep: true
      },
      {
        index: 2,
        name: 'Sélection Produits',
        component: 'Product',
        tablesHeight: '450',
        visitedStep: false
      },
      {
        index: 3,
        name: 'Confirmation',
        component: 'AssignConfirmation',
        tablesHeight: '450',
        visitedStep: false
      }
    ]);

    const tableHeight = computed(() => {
      const step = steps.value.find(s => s.index === currentStep.value);
      if (step === -1) {
        return '';
      }
      return step.tablesHeight;
    });

    watch(currentStep, (newValue, oldValue) => {
      const component = steps.value.find(step => step.index === parseInt(newValue));
      if (component) {
        dynamicComponent.value = component.component;
      }

      if (newValue < oldValue) {
        for (let i = newValue; i <= steps.value.length - 1; i += 1) {
          steps.value[i].visitedStep = false;
        }
      } else {
        steps.value[newValue - 1].visitedStep = true;
      }
    });

    const changeCurrentStep = value => {
      currentStep.value = value;
    };

    const submitProductCustomerAssign = async () => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      loading.value = true;

      const productsToSend = selectedAssignProducts.value.map(prod => ({
        product_uuid: prod.uuid,
        product_name: prod.name
      }));

      const selectedCustomersUuids = selectedAssignCustomer.value.map(c => c.uuid);

      const response = await addAllowedProductsToCustomers(selectedCustomersUuids, productsToSend, sendEmail.value);

      if (response.success) {
        sendNotification('success', 'Tous les clients ont été mis à jour avec succès');
      } else {
        sendNotification('error', 'Erreur lors de la mise à jour des clients');
      }

      loading.value = false;

      store.commit('setAlgoliaCustomers', []);

      selectedAssignCustomer.value = [];
      selectedAssignProducts.value = [];

      currentStep.value = 1;
      const notificationType = response.success ? 'success' : 'error';
      sendNotification(notificationType, response.message);
    };

    onBeforeMount(async () => {
      if (!store.state.isLazyLoaded.products) {
        loading.value = true;
        await store.dispatch('getProducts');
        loading.value = false;
      }
    });
    onMounted(() => {
      if (store.state.currentStep) {
        selectedAssignCustomer.value = store.state.customersToAssign;
        changeCurrentStep(store.state.currentStep);
        store.commit('setCurrentStep', null);
        store.commit('setCustomersToAssign', []);
      }
    });

    const isButtonEnable = computed(() => selectedAssignCustomer.value.length && selectedAssignProducts.value.length);

    const canUserPerformAction = computed(() => store.getters.canUserPerformAction);

    const productHeaders = [
      { text: 'Libellé', value: 'name', width: '30%', align: 'center' },
      { text: 'Text Eval', value: 'testEval', width: '20%', align: 'center' },
      { text: 'On demand', value: 'onDemand', width: '25%', align: 'center' },
      { text: 'Type de produit', value: 'offersLabel', width: '30%', align: 'center' }
    ];

    const clientHeaders = [
      { text: 'Nom', value: 'lastname', width: '15%', align: 'center' },
      { text: 'Prénom', value: 'firstname', width: '15%', align: 'center' },
      { text: 'Age', value: 'age', width: '10%', align: 'center' },
      { text: 'Espace digital', value: 'digitalAccountLabel', width: '15%', align: 'center' },
      { text: 'Niveau', value: 'equestrianLevelLabel', width: '15%', align: 'center' },
      { text: 'Email', value: 'email', width: '25%', align: 'center' }
    ];

    provide('submitProductCustomerAssign', submitProductCustomerAssign);
    provide('tableHeight', tableHeight);
    provide('productHeaders', productHeaders);
    provide('clientHeaders', clientHeaders);
    provide('selectedAssignCustomer', selectedAssignCustomer);
    provide('selectedAssignProducts', selectedAssignProducts);
    provide('sendAllowProductEmail', sendEmail);

    return { loading, isButtonEnable, canUserPerformAction, steps, currentStep, dynamicComponent, changeCurrentStep };
  }
};
</script>
